import { afterAll, afterEach, beforeAll, describe, expect, it } from '@jest/globals';
import { createTempDir, removeTempDir } from '@ms-cloudpack/test-utilities';
import { writeJsonSync } from '@ms-cloudpack/json-utilities';
import fs from 'fs';
import os from 'os';
import path from 'path';
import { getCachePath, getLocalCachePath } from './getCachePath.js';
import { getUserPreferencesPath, setMockUserPreferencesDir } from './getUserPreferencesPath.js';

describe('getCachePath', () => {
  let mockUserPreferencesDir: string;
  let mockUserPreferencesPath: string;

  beforeAll(() => {
    mockUserPreferencesDir = createTempDir();
    setMockUserPreferencesDir(mockUserPreferencesDir);
    mockUserPreferencesPath = getUserPreferencesPath();
  });

  afterEach(() => {
    fs.rmSync(mockUserPreferencesPath, { force: true });
  });

  afterAll(async () => {
    await removeTempDir(mockUserPreferencesDir);
  });

  it('returns default ~/.cloudpack path when no override or preference', async () => {
    const result = await getCachePath();
    expect(result).toBe(path.join(os.homedir(), '.cloudpack'));
  });

  it('returns absolute path when absolute overridePath is provided', async () => {
    // Use a platform-appropriate absolute path for testing
    const absolutePath = path.join(os.tmpdir(), 'custom-cache-path');
    const result = await getCachePath(absolutePath);
    expect(path.isAbsolute(result)).toBe(true);
    expect(result).toBe(absolutePath);
  });

  it('resolves relative overridePath to absolute path', async () => {
    const result = await getCachePath('.cloudpack-cache');
    expect(path.isAbsolute(result)).toBe(true);
    expect(result).toBe(path.resolve('.cloudpack-cache'));
  });

  it('resolves relative path with subdirectory to absolute path', async () => {
    const result = await getCachePath('./relative/cache/path');
    expect(path.isAbsolute(result)).toBe(true);
    expect(result).toBe(path.resolve('./relative/cache/path'));
  });

  it('uses cachePath from preferences when no override', async () => {
    // Use a platform-appropriate absolute path for testing
    const absolutePath = path.join(os.tmpdir(), 'preferences-cache');
    writeJsonSync(mockUserPreferencesPath, { cachePath: absolutePath });
    const result = await getCachePath();
    expect(result).toBe(absolutePath);
  });

  it('resolves relative cachePath from preferences to absolute', async () => {
    writeJsonSync(mockUserPreferencesPath, { cachePath: 'relative-from-prefs' });
    const result = await getCachePath();
    expect(path.isAbsolute(result)).toBe(true);
    expect(result).toBe(path.resolve('relative-from-prefs'));
  });

  it('prefers overridePath over preferences', async () => {
    const prefsPath = path.join(os.tmpdir(), 'preferences-cache');
    const overridePath = path.join(os.tmpdir(), 'override-cache');
    writeJsonSync(mockUserPreferencesPath, { cachePath: prefsPath });
    const result = await getCachePath(overridePath);
    expect(result).toBe(overridePath);
  });
});

describe('getLocalCachePath', () => {
  it('returns path inside node_modules/.cache/cloudpack', () => {
    const appPath = path.join(os.tmpdir(), 'app-path');
    const result = getLocalCachePath(appPath);
    expect(result).toBe(path.join(appPath, 'node_modules', '.cache', 'cloudpack'));
  });
});
